"use client";

import { useEffect, useRef, useState } from "react";
import { Target, TrendingUp, Search, Users, Award } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheNethillMethod() {
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [lineProgress, setLineProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const milestones = [{
    icon: Target,
    title: "Market Analysis",
    description: "Deep dive into Nigerian real estate trends and digital visibility gaps to identify untapped opportunities."
  }, {
    icon: Search,
    title: "Property Intelligence",
    description: "Leverage proprietary data to source verified, high-ROI properties across Lagos, Abuja, and Port Harcourt."
  }, {
    icon: TrendingUp,
    title: "SEO Strategy",
    description: "Craft bespoke digital campaigns that amplify your brand and drive targeted leads through search dominance."
  }, {
    icon: Users,
    title: "Client Collaboration",
    description: "Partner closely with you to align acquisition goals with marketing outcomes, ensuring measurable results."
  }, {
    icon: Award,
    title: "Proven Success",
    description: "Deliver portfolio growth and digital authority, backed by case studies and transparent performance metrics."
  }];
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current || !sectionRef.current) return;
      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const viewportHeight = window.innerHeight;
      const start = viewportHeight * 0.6;
      const end = viewportHeight * 0.3;
      if (sectionTop > start) {
        setLineProgress(0);
      } else if (sectionTop < end - sectionHeight) {
        setLineProgress(100);
      } else {
        const scrolled = start - sectionTop;
        const range = start - (end - sectionHeight);
        const progress = scrolled / range * 100;
        setLineProgress(Math.max(0, Math.min(100, progress)));
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section ref={sectionRef} className="relative bg-background py-24 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            The Nethill Method
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A proven, data-driven approach that fuses real estate expertise with cutting-edge digital marketing to deliver your unfair advantage.
          </p>
        </div>

        <div ref={timelineRef} className="relative">
          {}
          <div className="hidden lg:grid lg:grid-cols-5 gap-8 relative">
            {}
            <div className="absolute top-12 left-0 right-0 h-0.5 bg-border" style={{
            width: "calc(100% - 80px)",
            left: "40px"
          }} />
            
            {}
            <div className="absolute top-12 left-0 h-0.5 bg-primary transition-all duration-300 ease-out" style={{
            width: `calc(${lineProgress}% - 80px)`,
            left: "40px",
            maxWidth: "calc(100% - 80px)"
          }} />

            {milestones.map((milestone, i) => {
            const Icon = milestone.icon;
            return <div key={i} data-index={i} className="relative flex flex-col items-center group">
                  {}
                  <div className="relative z-10 w-20 h-20 rounded-full bg-primary text-foreground-primary flex items-center justify-center shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] group-hover:shadow-strong group-hover:-translate-y-1 group-hover:bg-primary-hover">
                    <Icon className="w-9 h-9" />
                  </div>

                  {}
                  <div className="mt-8 text-center px-2">
                    <h3 className="text-lg font-semibold text-foreground mb-2 transition-colors duration-[var(--duration-medium)] group-hover:text-primary">
                      {milestone.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="lg:hidden flex flex-col gap-12 relative pl-12">
            {}
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-border" />
            
            {}
            <div className="absolute left-5 top-0 w-0.5 bg-primary transition-all duration-300 ease-out" style={{
            height: `${lineProgress}%`
          }} />

            {milestones.map((milestone, i) => {
            const Icon = milestone.icon;
            return <div key={i} data-index={i} className="relative flex items-start gap-6 group">
                  {}
                  <div className="relative z-10 w-16 h-16 rounded-full bg-primary text-foreground-primary flex items-center justify-center shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] group-hover:shadow-strong group-hover:-translate-y-1 group-hover:bg-primary-hover flex-shrink-0">
                    <Icon className="w-7 h-7" />
                  </div>

                  {}
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-semibold text-foreground mb-2 transition-colors duration-[var(--duration-medium)] group-hover:text-primary">
                      {milestone.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}